import React from 'react'
import {
  Loader,
  Panel,
  VStack,
  Heading,
  Text,
  useToaster,
  Message,
} from 'rsuite'
import { Link, useNavigate } from 'react-router'

import CreateEditUserForm, {
  CreateEditUserFormValue,
} from 'shared/CreateEditUserForm/CreateEditUserForm'

import { useQueryParams } from 'app/lib/utils'
import { useInvite } from 'app/lib/invites'

type SignupQueryParams = {
  c?: string
}

async function createUser(
  userDto: CreateEditUserFormValue,
  inviteCode: string,
): Promise<void> {
  const response = await fetch('/auth/sign-up', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ ...userDto, inviteCode } as CreateUserDto),
  })

  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(errorText || 'Error creating user')
  }
}

const SignUp: React.FC = (): React.ReactElement => {
  const queryParams = useQueryParams<SignupQueryParams>()
  const { c: inviteCode } = queryParams

  const navigate = useNavigate()
  const toaster = useToaster()

  const {
    data: invite,
    isLoading,
    isError,
    error,
  } = useInvite(inviteCode ?? '', {
    enabled: !!inviteCode,
    retry: (_, error) => {
      // Check for specific error message from server indicating invalid
      // invite code
      if (
        error instanceof Error &&
        error.message.includes('Invite not found')
      ) {
        // don't retry
        return false
      }
      // retry other errors
      return true
    },
    throwOnError: (error) => {
      if (error instanceof Error) {
        // Check for specific error message from server indicating invalid
        // invite code
        if (error.message.includes('Invite not found')) {
          // don't throw error to component, we'll handle it in the UI
          return false
        }
      }

      // throw other errors to component
      return true
    },
  })

  if (error) {
    console.error('Error fetching invite information:', error)
  }

  if (isLoading) {
    return <Loader backdrop vertical center content="Loading..." />
  }

  if (isError) {
    return (
      <Panel bordered>
        <Heading>Invalid Invite Code</Heading>
        <Text>
          The invite code provided is invalid. Please check the link you used
          and try again. If you believe this is an error, please contact us via
          the{' '}
          <Link to="/contact" viewTransition>
            Contact Page
          </Link>
          .
        </Text>
      </Panel>
    )
  }

  if (inviteCode && !invite) {
    return <Loader backdrop vertical center content="Loading..." />
  }

  const okCallback = async (user: CreateEditUserFormValue) => {
    try {
      await createUser(user, inviteCode ?? '')

      toaster.push(
        <Message type="success" closable>
          <Text weight="bold">Success!</Text>
          <Text>Your account has been created. Redirecting to login...</Text>
        </Message>,
      )

      setTimeout(() => {
        navigate(`/login?c=${inviteCode}`, { viewTransition: true })
      }, 3000)
    } catch (error) {
      console.error('Error creating user:', error)

      toaster.push(
        <Message type="error" closable>
          <Text weight="bold">Error!</Text>
          <Text>
            An error occurred while creating your account. Please try again.
          </Text>
        </Message>,
      )
    }
  }

  return (
    <Panel bordered>
      <VStack>
        {inviteCode ? (
          <>
            <Heading>Sign Up</Heading>
            <Text>
              {invite?.organizationName} has invited you to join Techferral.
              Create an account and get started sharing your referral links and
              QR codes to earn cash rewards. If you already have an account, log
              in{' '}
              <Link to={`/login?c=${inviteCode}`} viewTransition>
                here
              </Link>
              .
            </Text>
            <CreateEditUserForm
              okCallback={okCallback}
              requirePasswordConfirmation
              createButtonText="Sign Up"
            />
          </>
        ) : (
          <>
            <Heading>Thanks for your interest!</Heading>
            <Text>
              In order to sign up for Techferral, you must first be invited by
              an organization. If you arrived here via an invite email, please
              click the "Sign Up" button in that email to create your account.
            </Text>
            <Text>
              If you're an organization interested in using Techferral, please
              contact us via email at{' '}
              <a href="mailto:contact@techferral.com">contact@techferral.com</a>
              .
            </Text>
          </>
        )}
      </VStack>
    </Panel>
  )
}

export default SignUp
